import React, {useMemo, useState} from 'react';
import {ReservationDTO} from "../../reservation/types";
import {Alert, Button, Card, Descriptions, Form, message, Popconfirm, Select, Tag} from "antd";
import {CarOutlined, CopyOutlined, ExportOutlined} from "@ant-design/icons";
import {useClipboard} from "../../commons/utils";
import {Link} from "react-router-dom";
import axios from "axios";
import {LoadingOverlay} from "../common/loading/component";
import {useGoogleReCaptcha} from "react-google-recaptcha-v3";
import {useTranslation} from "react-i18next";
import {Label} from "../../commons/translations/Label";
import {useNavigate} from "react-router";

export const ReservationCard: React.FC<{rsv: ReservationDTO, reload: () => void}> = (props) => {
    const {t} = useTranslation();
    const {rsv, reload} = props;
    const { executeRecaptcha } = useGoogleReCaptcha();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [completed, setCompleted] = useState(false);
    const canRsvp = ['RESERVED', 'RSVPED'].includes(rsv.status);
    const areReservationsOpen = rsv.rsvpOpensAt <= new Date().getTime();
    const {copyToClipboard} = useClipboard()
    const navigate = useNavigate();

    const onFinish = async (values: {status: string}) => {
        if (!executeRecaptcha) {
            return;
        }
        executeRecaptcha('rsvp').then(async (captcha) => {
            setLoading(true);
            try {
                await axios.put(`/api/reservations/${rsv.id}/status`, { status: values.status, captcha });
                message.success(t('myReservations.success.update'));
                setCompleted(true);
                reload();
            } catch (error) {
                message.error(t('myReservations.errors.update'));
            } finally {
                setLoading(false);
            }
        });
    };

    const statusOptions = useMemo<{label:JSX.Element, value: string}[]>(() => {
        const options = [];
        if (rsv.status === 'RESERVED' && areReservationsOpen) {
            options.push({ label: <span style={{color: 'green'}}>{t('myReservations.card.status.verify')}</span>, value: 'RSVPED' });
        }
        options.push({ label: <span style={{color: 'red'}}>{t('myReservations.card.status.cancel')}</span>, value: 'CANCELED' });
        return options;
    }, [rsv.status]);


    return (
        <Card title={`${rsv.game?.title || t('myReservations.card.game')} / ${rsv.table?.title}`}>
            {loading && <LoadingOverlay working={loading} />}
            <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label={<Label trl="myReservations.card.location" />}>{rsv.location}</Descriptions.Item>
                <Descriptions.Item label={<Label trl="myReservations.card.code" />}>
                    <Button icon={<CopyOutlined/>} onClick={() => copyToClipboard(rsv.code)}>{rsv.code}</Button>
                </Descriptions.Item>
                <Descriptions.Item label={<Label trl="myReservations.card.seats" />}>{rsv.seats}</Descriptions.Item>
                <Descriptions.Item label={<Label trl="myReservations.card.link" />}>
                    <Button
                        icon={<ExportOutlined/>}
                        onClick={() => navigate(`/reserve/${rsv.eventTag}`)}>
                        <Label trl={'myReservations.card.link.details'} />
                    </Button>
                </Descriptions.Item>
                <Descriptions.Item label={<Label trl="myReservations.card.reservedAt" />}>
                    {new Date(rsv.reservedAt).toLocaleString()}
                </Descriptions.Item>
                {rsv?.navigationLink && <Descriptions.Item label={<Label trl="myReservations.card.getThere" />}>
                    <Link to={rsv.navigationLink} target="_blank">
                        <Button icon={<CarOutlined/>} type={'primary'} />
                    </Link>
                </Descriptions.Item>}
                <Descriptions.Item label={<Label trl="myReservations.card.status" />}>
                    <Tag color={
                        rsv.status === 'RESERVED' ? 'cyan'
                            : rsv.status === 'RSVPED' ? 'success'
                                : rsv.status === 'CANCELED' ? 'red'
                                    : 'purple'
                    } >{rsv.status}</Tag>
                </Descriptions.Item>
            </Descriptions>
            
            {canRsvp && !completed && (
                <Form 
                    form={form} 
                    layout="inline" 
                    style={{marginTop: 16, justifyContent: 'space-between'}}
                    onFinish={onFinish}
                    initialValues={{status: rsv.status === 'RESERVED' && areReservationsOpen ? 'RSVPED' : 'CANCELED'}}
                >
                    {!areReservationsOpen && (
                        <p>
                            <Alert
                                type="warning"
                                showIcon={true}
                                message={<Label trl={'myReservations.rsvps.not.open.yet'} segments={[{
                                    key:'#0', value: new Date(rsv.rsvpOpensAt).toLocaleString()
                                }]}/>}
                            />
                        </p>
                    )}
                    <Form.Item name="status" style={{flex: 1}}>
                        <Select>
                            {statusOptions.map(opt => (
                                <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Popconfirm
                            title={t('myReservations.card.confirm.update')}
                            onConfirm={form.submit}
                            okText={t('common.yes')}
                            cancelText={t('common.no')}
                        >
                            <Button type="primary" htmlType="button">
                                {t('myReservations.card.buttons.rsvp')}
                            </Button>
                        </Popconfirm>
                    </Form.Item>
                </Form>
            )}
        </Card>
    )
}